import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, IconButton, Tab, type TabProps, Tooltip } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';
import type { MouseEvent } from 'react';
import React, { useCallback, useMemo } from 'react';
import { SIZES } from '../constants/constants';
import { useShallowMergeSxProps } from '../hooks/useShallowMergeSxProps';
import I18n, { useI18n } from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { SAVED_FILTERS_CONSTANTS } from '../types/savedFilters';

const SIDE_ACTION_SIZE = 20;
const DRAG_ICON_SIZE = 16;

const TAB_BASE_SX: SxProps<Theme> = {
    justifyContent: 'center',
    textAlign: 'center',
    [`&:hover .${SAVED_FILTERS_CONSTANTS.PINNED_TAB_DRAG_HANDLE_CLASS}`]: {
        flexBasis: SIDE_ACTION_SIZE,
        width: SIDE_ACTION_SIZE,
        minWidth: SIDE_ACTION_SIZE,
        opacity: 1
    },
    [`&:hover .${SAVED_FILTERS_CONSTANTS.PINNED_TAB_UNPIN_BUTTON_CLASS}`]: {
        flexBasis: SIDE_ACTION_SIZE,
        width: SIDE_ACTION_SIZE,
        minWidth: SIDE_ACTION_SIZE,
        opacity: 0.7,
        pointerEvents: 'auto'
    }
};

const LABEL_CONTAINER_SX: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minWidth: 0
};

const SIDE_ACTION_SX: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 0px',
    width: 0,
    minWidth: 0,
    height: SIDE_ACTION_SIZE,
    overflow: 'hidden',
    opacity: 0,
    boxSizing: 'border-box'
};

const SIDE_ACTION_VISIBLE_SX: SxProps<Theme> = {
    ...SIDE_ACTION_SX,
    flexBasis: SIDE_ACTION_SIZE,
    width: SIDE_ACTION_SIZE,
    minWidth: SIDE_ACTION_SIZE,
    opacity: 1
};

const DRAG_ICON_SX: SxProps<Theme> = {
    fontSize: DRAG_ICON_SIZE,
    color: 'inherit',
    opacity: 0.7,
    pointerEvents: 'none'
};

const LABEL_TEXT_SX: SxProps<Theme> = {
    display: 'block',
    flex: '1 1 auto',
    minWidth: 0,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
};

const UNPIN_BUTTON_SX: SxProps<Theme> = {
    ...SIDE_ACTION_SX,
    p: 0,
    color: 'inherit',
    pointerEvents: 'none',
    '&:hover': { opacity: 1 }
};

const CLOSE_ICON_SX: SxProps<Theme> = {
    fontSize: 14
};

type SortablePinnedFilterTabProps = {
    value: string;
    label: string;
    onUnpin: (preferenceId: string) => void;
} & Omit<TabProps, 'label' | 'value'>;

export default function SortablePinnedFilterTab({
    value,
    label,
    onUnpin,
    onClick,
    style,
    sx,
    ...tabProps
}: SortablePinnedFilterTabProps) {
    const t = useI18n();
    const unpinLabel = t('filter.savedFilter.unpinTab');
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: value });

    const handleUnpin = useCallback(
        (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            onUnpin(value);
        },
        [onUnpin, value]
    );

    const handleClick = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            onClick?.(event);
        },
        [onClick]
    );

    const tabSx = useShallowMergeSxProps(TAB_BASE_SX, sx);

    const tabStyle = useMemo(
        () => ({
            ...style,
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.7 : 1,
            zIndex: isDragging ? 1 : undefined,
            cursor: isDragging ? 'grabbing' : 'grab'
        }),
        [style, transform, transition, isDragging]
    );

    const { role: _role, tabIndex: _tabIndex, ...sortableAttributes } = attributes;

    return (
        <Tab
            {...sortableAttributes}
            {...tabProps}
            {...listeners}
            ref={setNodeRef}
            value={value}
            data-testid={`pinned-filter-tab-${value}`}
            onClick={handleClick}
            sx={tabSx}
            style={tabStyle}
            label={
                <Box sx={LABEL_CONTAINER_SX}>
                    <Box
                        className={SAVED_FILTERS_CONSTANTS.PINNED_TAB_DRAG_HANDLE_CLASS}
                        data-testid={`pinned-filter-tab-drag-${value}`}
                        sx={isDragging ? SIDE_ACTION_VISIBLE_SX : SIDE_ACTION_SX}
                    >
                        <Icons.DragIndicator sx={DRAG_ICON_SX} />
                    </Box>
                    <Tooltip title={label}>
                        <Box component="span" sx={LABEL_TEXT_SX}>
                            {label}
                        </Box>
                    </Tooltip>
                    <Tooltip title={<I18n token="filter.savedFilter.unpinTab" />}>
                        <IconButton
                            className={SAVED_FILTERS_CONSTANTS.PINNED_TAB_UNPIN_BUTTON_CLASS}
                            size={SIZES.small}
                            aria-label={unpinLabel}
                            data-testid={`pinned-filter-tab-unpin-${value}`}
                            onClick={handleUnpin}
                            onPointerDown={(event) => event.stopPropagation()}
                            onMouseDown={(event) => event.stopPropagation()}
                            sx={UNPIN_BUTTON_SX}
                        >
                            <Icons.Close sx={CLOSE_ICON_SX} />
                        </IconButton>
                    </Tooltip>
                </Box>
            }
        />
    );
}
